import {
  forwardRef,
  useEffect,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
} from "react"

import { Button } from "@coval/ui/button"
import { Flag, Plus } from "lucide-react"

import { formatTime } from "@/lib/audio-player-utils"

import { AudioRegionBoundsFields } from "./AudioRegionBoundsFields"

export interface AudioMarker {
  id: string
  time: number
  label: string
  type: "interruption" | "issue" | "note"
}

interface AddAudioMarkerButtonProps {
  visible: boolean
  /**
   * The point on the waveform's top edge the hover is over, in the coordinates
   * of the element this button is positioned against. The bubble centres itself
   * on that point and sits above it, so its arrow lands on the hovered sample.
   */
  position: { x: number; y: number } | null
  onAdd: () => void
  onMouseEnter: MouseEventHandler<HTMLDivElement>
  onMouseLeave: MouseEventHandler<HTMLDivElement>
}

export function AddAudioMarkerButton({
  visible,
  position,
  onAdd,
  onMouseEnter,
  onMouseLeave,
}: AddAudioMarkerButtonProps) {
  if (!visible || !position) {
    return null
  }

  return (
    <div
      className="absolute z-20 -translate-x-1/2 -translate-y-full pb-1"
      style={{
        left: position.x,
        top: position.y,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="relative">
        <Button
          onClick={onAdd}
          className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
          aria-label="Add marker"
        >
          <Plus size={16} />
        </Button>
        <div
          className="absolute w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-500"
          style={{
            left: "50%",
            top: "100%",
            transform: "translateX(-50%)",
          }}
        />
      </div>
    </div>
  )
}

interface AudioMarkerFlagsProps {
  markers: AudioMarker[]
  duration: number
  onEdit: (marker: AudioMarker) => void
}

export function AudioMarkerFlags({
  markers,
  duration,
  onEdit,
}: AudioMarkerFlagsProps) {
  return markers.map((marker) => (
    <div
      key={marker.id}
      data-marker-flag
      role="button"
      tabIndex={0}
      className="absolute z-10 bg-red-500 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-red-600 transition-colors flag-container"
      style={{
        left: `${(marker.time / (duration || 1)) * 100}%`,
        top: "5px",
        transform: "translateX(-50%)",
      }}
      aria-label={`Edit marker: ${marker.label} at ${formatTime(marker.time)}`}
      onClick={(event) => {
        event.stopPropagation()
        onEdit(marker)
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          event.stopPropagation()
          onEdit(marker)
        }
      }}
    >
      <Flag size={12} />
    </div>
  ))
}

interface AudioRegionSelectionMenuProps {
  selectedRegionId: string | null
  selectedPosition: { x: number; y: number } | null
  clampedPosition: { left: number; top: number } | null
  menuRef: RefObject<HTMLDivElement | null>
  /** The selected segment's bounds, when they can be edited by typing. */
  bounds?: { start: number; end: number; duration: number } | null
  /** Moves focus into the menu once it opens, for a segment opened by key. */
  focusOnOpen?: boolean
  extra?: (regionId: string) => ReactNode
  onBoundsChange?: (regionId: string, start: number, end: number) => void
  onDelete: () => void
  onDismiss: () => void
}

export function AudioRegionSelectionMenu({
  selectedRegionId,
  selectedPosition,
  clampedPosition,
  menuRef,
  bounds,
  focusOnOpen = false,
  extra,
  onBoundsChange,
  onDelete,
  onDismiss,
}: AudioRegionSelectionMenuProps) {
  const isOpen = Boolean(selectedRegionId && selectedPosition)
  useEffect(() => {
    if (isOpen && focusOnOpen) {
      menuRef.current?.querySelector<HTMLElement>("input, button")?.focus()
    }
  }, [focusOnOpen, isOpen, menuRef, selectedRegionId])

  if (!selectedRegionId || !selectedPosition) {
    return null
  }

  return (
    // Escape from any control in the menu dismisses it.
    <div
      role="presentation"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault()
          event.stopPropagation()
          onDismiss()
        }
      }}
    >
      <div
        ref={menuRef}
        role="group"
        aria-label="Selected segment"
        className="fixed z-30 flex flex-col gap-2 rounded-md border border-coval-border bg-surface-default px-2 py-1.5 shadow"
        style={
          clampedPosition ?? {
            left: selectedPosition.x + 10,
            top: selectedPosition.y + 10,
          }
        }
      >
        {bounds && onBoundsChange && (
          <AudioRegionBoundsFields
            duration={bounds.duration}
            start={bounds.start}
            end={bounds.end}
            onChange={(start, end) =>
              onBoundsChange(selectedRegionId, start, end)
            }
          />
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-coval-text-muted">Selected</span>
          {extra?.(selectedRegionId)}
          <Button
            variant="danger"
            className="h-6 text-xs"
            onClick={() => {
              // The deleted segment can't take focus back, so a keyboard user
              // lands on the waveform's seek control instead of the page.
              const menu = menuRef.current
              const seekControl = menu
                ?.closest("[data-audio-waveform-well]")
                ?.querySelector<HTMLElement>('[role="slider"]')
              const hadFocus = Boolean(menu?.contains(document.activeElement))
              onDelete()
              if (hadFocus) {
                seekControl?.focus()
              }
            }}
            aria-label="Delete highlight"
          >
            Delete
          </Button>
          <Button
            variant="ghost"
            className="h-6 text-xs"
            onClick={onDismiss}
            aria-label="Clear selection"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Narrowest tail that fits the recording-ended label. */
const TAIL_LABEL_MIN_PIXELS = 104

/** A timeline may continue past the recording, but that space has no waveform. */
export function AudioTrackTail({ width }: { width: number }) {
  if (width <= 0) {
    return null
  }
  return (
    <div
      className="absolute inset-y-0 right-0 flex items-end border-l border-dashed border-coval-border-strong bg-surface-1/40 pointer-events-none"
      style={{ width }}
    >
      {width >= TAIL_LABEL_MIN_PIXELS && (
        <span className="whitespace-nowrap px-1.5 pb-1 text-xs text-coval-text-muted">
          Recording ended
        </span>
      )}
    </div>
  )
}

export const AudioWaveformContainer = forwardRef<
  HTMLDivElement,
  {
    compact: boolean
    duration?: number
    tailPx: number
  }
>(function AudioWaveformContainer({ compact, duration, tailPx }, ref) {
  return (
    <div
      ref={ref}
      data-audio-timeline-duration={duration}
      className={compact ? "w-full" : "absolute inset-y-0 left-0"}
      style={
        compact ? { width: `calc(100% - ${tailPx}px)` } : { right: tailPx }
      }
    />
  )
})
